import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import { PageHeader, Modal, Badge, useToast, Skeleton } from '../ui';
import { useLang } from '../Layout';
import { compressImage } from '../PhotoInput';
import { FaCamera, FaChevronLeft, FaChevronRight, FaCheck } from 'react-icons/fa6';

const T = {
  bn: {
    title: 'হাজিরা', ownerSub: 'কর্মীদের হাজিরা ও ছবি', managerSub: 'দৈনিক হাজিরা নিন + ছবি',
    total: 'মোট', present: 'উপস্থিত', absent: 'অনুপস্থিত', leave: 'ছুটি',
    save: 'হাজিরা সংরক্ষণ করুন', saved: 'হাজিরা সংরক্ষিত হয়েছে',
    noWorker: 'এই দিনে কোনো কর্মী নেই।', photo: 'ছবি', remove: 'মুছুন', today: 'আজ',
  },
  en: {
    title: 'Attendance', ownerSub: 'Worker attendance & photos', managerSub: 'Mark daily attendance + photos',
    total: 'Total', present: 'Present', absent: 'Absent', leave: 'Leave',
    save: 'Save Attendance', saved: 'Attendance saved', noWorker: 'No workers for this date.',
    photo: 'Photo', remove: 'Remove', today: 'Today',
  },
};

function PhotoButton({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { onChange(await compressImage(file)); } catch { /* ignore */ } finally { setBusy(false); e.target.value = ''; }
  }
  return (
    <div className="flex items-center gap-2">
      {value ? (
        <div className="relative">
          <img src={value} alt="attendance" className="w-10 h-10 rounded-lg object-cover border border-ink-200" />
          <button type="button" onClick={() => onChange(null)}
            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">×</button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-10 h-10 rounded-lg border border-dashed border-ink-300 bg-ink-50 text-ink-400 hover:border-brand-400 hover:text-brand-600 flex items-center justify-center transition">
          {busy ? <span className="w-3.5 h-3.5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /> : <FaCamera />}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
    </div>
  );
}

export default function Attendance({ role }) {
  const { lang } = useLang(); const t = T[lang];
  const isManager = role === 'manager';
  const base = isManager ? '/manager' : '/owner';
  const { toast } = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [workers, setWorkers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [perContractor, setPerContractor] = useState([]);
  const [marks, setMarks] = useState({});
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [filterId, setFilterId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`${base}/attendance?date=${date}`).then(({ data }) => {
      if (isManager) {
        const init = {}, ph = {};
        data.workers.forEach((w) => { init[w.id] = w.marked_status || 'present'; ph[w.id] = w.marked_photo || null; });
        setMarks(init); setPhotos(ph);
        setWorkers(data.workers);
        setPerContractor(data.summary || []);
        setSummary({ total: data.workers.length, present: 0, absent: 0 });
      } else {
        setWorkers(data.attendance);
        setSummary(data.summary);
        setPerContractor(data.per_contractor || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [date]);

  function shiftDay(d) {
    const dt = new Date(`${date}T12:00:00`);
    dt.setDate(dt.getDate() + d);
    setDate(dt.toISOString().slice(0, 10));
  }

  async function save() {
    const records = workers.map((w) => ({
      worker_id: w.id,
      status: marks[w.id] || 'present',
      photo: photos[w.id] || null,
    }));
    await api.post(`${base}/attendance/mark`, { date, records });
    toast(t.saved, 'success');
    load();
  }

  // Unique contractors (for the filter chips)
  const filterOptions = [];
  const seenIds = new Set();
  workers.forEach((w) => {
    if (w.contractor_id != null && !seenIds.has(w.contractor_id)) { seenIds.add(w.contractor_id); filterOptions.push({ id: w.contractor_id, name: w.contractor_name || '—' }); }
  });

  // Group manager list by contractor for a cleaner sheet
  const groups = [];
  const groupMap = {};
  const filteredWorkers = filterId ? workers.filter((w) => w.contractor_id === filterId) : workers;
  filteredWorkers.forEach((w) => {
    const key = w.contractor_name || '—';
    if (!groupMap[key]) { groupMap[key] = { name: key, trade: w.contractor_trade, rows: [] }; groups.push(groupMap[key]); }
    groupMap[key].rows.push(w);
  });

  const statusBtn = (val) => ({
    present: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    absent: 'text-rose-700 bg-rose-50 border-rose-200',
    leave: 'text-amber-700 bg-amber-50 border-amber-200',
  }[val] || 'bg-white border-ink-200 text-ink-500');

  return (
    <div className="space-y-4">
      <PageHeader title={t.title} subtitle={isManager ? t.managerSub : t.ownerSub}
        actions={
          <div className="flex items-center gap-2 bg-white border border-ink-100 rounded-xl p-1">
            <button onClick={() => shiftDay(-1)} className="p-1.5 rounded-lg hover:bg-ink-50 text-ink-500"><FaChevronLeft size={13} /></button>
            <input type="date" className="text-sm font-medium text-ink-700 bg-transparent focus:outline-none" value={date} onChange={(e) => setDate(e.target.value)} />
            <button onClick={() => shiftDay(1)} className="p-1.5 rounded-lg hover:bg-ink-50 text-ink-500"><FaChevronRight size={13} /></button>
          </div>
        } />

      {/* Contractor filter chips */}
      {filterOptions.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button onClick={() => setFilterId(null)}
            className={`shrink-0 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition ${filterId === null ? 'bg-ink-900 text-white border-ink-900' : 'bg-white border-ink-200 text-ink-500 hover:border-ink-400'}`}>
            {lang === 'bn' ? 'সব সাইড' : 'All sides'}
          </button>
          {filterOptions.map((opt) => (
            <button key={opt.id} onClick={() => setFilterId(opt.id)}
              className={`shrink-0 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition ${filterId === opt.id ? 'bg-brand-600 text-white border-brand-600' : 'bg-white border-ink-200 text-ink-600 hover:border-brand-300'}`}>
              {opt.name}
            </button>
          ))}
        </div>
      )}

      {/* Per-contractor summary */}
      {(filterId ? perContractor.filter((c) => c.contractor_id === filterId) : perContractor).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(filterId ? perContractor.filter((c) => c.contractor_id === filterId) : perContractor).map((c) => (
            <div key={c.contractor_id} className="bg-white rounded-xl border border-ink-100 shadow-soft p-3.5">
              <div className="text-sm font-semibold text-ink-800 truncate">{c.contractor_name}</div>
              <div className="text-[11px] text-ink-400">{lang === 'bn' ? 'মোট' : 'Total'} {c.total} জন</div>
              <div className="flex gap-2 mt-2 text-[11px] font-semibold">
                <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700">{t.present} {c.present ?? 0}</span>
                <span className="px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-700">{t.absent} {c.absent ?? 0}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /></div> : null}

      {/* Worker sheets */}
      {!loading && workers.length > 0 && (
        isManager ? (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.name} className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
                <div className="px-4 py-2.5 bg-ink-900 text-white flex items-center justify-between">
                  <span className="text-sm font-semibold">{g.name}</span>
                  <span className="text-[11px] text-ink-400">{g.rows.length} জন</span>
                </div>
                <div className="divide-y divide-ink-50">
                  {g.rows.map((w) => (
                    <div key={w.id} className="px-4 py-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-xs font-bold shrink-0">{w.worker_name?.[0]}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-ink-800 truncate">{w.worker_name}</div>
                      </div>
                      <div className="flex gap-1.5">
                        {['present', 'absent', 'leave'].map((val) => (
                          <button key={val} type="button"
                            onClick={() => setMarks({ ...marks, [w.id]: val })}
                            className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition ${marks[w.id] === val ? statusBtn(val) : 'bg-white border-ink-200 text-ink-400'}`}>
                            {val === 'present' ? t.present : val === 'absent' ? t.absent : t.leave}
                          </button>
                        ))}
                      </div>
                      <PhotoButton value={photos[w.id]} onChange={(p) => setPhotos({ ...photos, [w.id]: p })} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={save} className="btn-primary w-full py-3.5 font-semibold flex items-center justify-center gap-2">
              <FaCheck /> {t.save}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
            <table className="overflow-x-auto w-full text-sm">
              <thead><tr className="bg-ink-50/50 text-left text-xs font-semibold text-ink-400 uppercase"><th className="px-4 py-3">নাম</th><th className="px-4 py-3">কন্ট্রাক্টর</th><th className="px-4 py-3">স্ট্যাটাস</th><th className="px-4 py-3">{t.photo}</th></tr></thead>
              <tbody>
                {filteredWorkers.map((w) => (
                  <tr key={w.id} className="border-t border-ink-100">
                    <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-xs font-bold">{w.worker_name?.[0]}</div><span className="font-medium text-ink-800">{w.worker_name}</span></div></td>
                    <td className="px-4 py-3 text-ink-500">{w.contractor_name || '—'}</td>
                    <td className="px-4 py-3"><Badge color={w.status}>{t[w.status] || w.status}</Badge></td>
                    <td className="px-4 py-3">
                      {w.photo ? (
                        <button onClick={() => setViewPhoto(w.photo)} className="w-10 h-10 rounded-lg overflow-hidden border border-ink-100"><img src={w.photo} alt="" className="w-full h-full object-cover" /></button>
                      ) : <span className="text-ink-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!loading && filteredWorkers.length === 0 && (
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft text-center py-14 text-ink-400 text-sm">{t.noWorker}</div>
      )}

      {/* Photo viewer */}
      <Modal open={!!viewPhoto} onClose={() => setViewPhoto(null)} title={t.photo}>
        <img src={viewPhoto} alt="attendance" className="w-full rounded-xl" />
      </Modal>
    </div>
  );
}